'use client';

import { c as _c } from "react/compiler-runtime";
import type { ListRecommendPolicyDto } from '__generated__/api/auth';
import { SvgChevrondown, SvgChevronup } from '@common/icon';
import { cn } from '@core/utils';
import { useSuspenseQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { usePathname, useSearchParams } from 'next/navigation';
import { AuthQueryOptions } from '@/api';
import { useTimelineScrollHistory } from '@/app/_hooks/useTimelineScrollHistory';
import { useAuthStore } from '@/stores/AuthStore';
import { isDeadlinePolicy } from '../_lib/utils';
import { TimelineCard } from './TimelineCard';
const filterPolicies = (policies: ListRecommendPolicyDto[], filterType: 'new' | 'deadline' | 'scrap') => {
  if (filterType === 'new') return policies.filter(policy => policy.newPolicy);
  if (filterType === 'deadline') return policies.filter(policy => isDeadlinePolicy(policy.dday));
  return policies.filter(policy => policy.wishlistYN);
};
export const PolicyTimeline = () => {
  const $ = _c(5);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      ...AuthQueryOptions["get:/wello/v2/auth/recommend-policy"]({
        query: {
          recommendPolicySort: "PRIORITY",
          recommendViewType: "TIMELINE"
        }
      })
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    data
  } = useSuspenseQuery(t0);
  const age = useAuthStore(_temp);
  const searchParams = useSearchParams();
  const filter = searchParams?.get("filter") as 'available' | 'new' | 'deadline' | 'scrap' | null;
  const pathname = usePathname();
  let t1;
  if ($[1] !== searchParams) {
    t1 = searchParams?.toString();
    $[1] = searchParams;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const query = t1;
  const fullPath = query ? `${pathname}?${query}` : pathname;
  const currentYear = dayjs().year();
  const prevYear = currentYear - 1;
  const past = data.context?.pastContents ?? [];
  const deadline = data.context?.deadlineContents ?? [];
  const current = data.context?.contents ?? [];
  const future = data.context?.futureContents ?? [];
  const baseSections = [{
    id: "past",
    title: "\uC791\uB144\uAE4C\uC9C0 \uB9C8\uAC10\uB41C \uC815\uCC45",
    subtitle: age ? `~ ${prevYear}, 만 ${age - 1}세 이전` : `${prevYear}년 이전`,
    count: past.length,
    totalCount: past.length,
    contents: past
  }, {
    id: "thisYear",
    title: "\uC62C\uD574\uC758 \uB9C8\uAC10\uB41C \uC815\uCC45",
    subtitle: age ? `${currentYear}, 만 ${age}세` : `${currentYear}년`,
    count: deadline.length,
    totalCount: deadline.length,
    contents: deadline
  }, {
    id: "current",
    title: "\uC9C0\uAE08 \uC2E0\uCCAD \uAC00\uB2A5\uD55C \uC815\uCC45",
    subtitle: age ? `${currentYear}, 만 ${age}세` : `${currentYear}년`,
    count: current.length,
    totalCount: current.length,
    contents: current
  }, {
    id: "future",
    title: "\uACE7 \uC2E0\uCCAD \uAC00\uB2A5\uD55C \uC815\uCC45",
    subtitle: "",
    count: future.length,
    totalCount: future.length,
    contents: future
  }] as const;
  const sections = baseSections.map(section => {
    if (!filter || filter === "available") {
      return section;
    }
    const shouldApplyFilter = filter === "scrap" || filter === "new" && (section.id === "current" || section.id === "thisYear" || section.id === "future") || filter === "deadline" && (section.id === "current" || section.id === "thisYear");
    if (!shouldApplyFilter) {
      return {
        ...section,
        count: 0
      };
    }
    const filtered = filterPolicies(section.contents, filter);
    return {
      ...section,
      contents: filtered.length > 0 ? filtered : section.contents,
      count: filtered.length
    };
  });
  const {
    sectionRefs,
    expandedIds,
    setExpandedIds
  } = useTimelineScrollHistory({
    sections,
    filter,
    fullPath
  });
  const toggleSection = id => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };
  const t2 = "flex min-h-[calc(100vh-270px)] flex-col gap-12 px-20 pb-40";
  const t3 = sections.map((section_0, idx) => {
    const isExpanded = expandedIds.has(section_0.id);
    const isCurrent = section_0.id === "current";
    const isLast = idx === sections.length - 1;
    const isNextCurrent = sections[idx + 1]?.id === "current";
    return <div key={section_0.id} className="animate-fade-in relative flex items-start gap-10" style={{
      animationDelay: `${idx * 100}ms`
    }}>{!isLast && <div className={cn("absolute left-5 top-36 z-0 h-[calc(100%+6px)] w-2 transition-all duration-500", isCurrent ? "bg-gradient-to-b from-B-500 to-Gray-300" : isNextCurrent ? "bg-gradient-to-b from-Gray-300 to-B-500" : isExpanded ? "bg-Gray-400" : "bg-Gray-300")} />}<div className="absolute left-0 top-30 z-[1]"><div className={cn("size-12 rounded-pill transition-transform duration-300", isCurrent ? "bg-B-500" : isExpanded ? "bg-Gray-400" : "bg-Gray-300", isExpanded && "scale-110")} /></div><div className="flex-1 pl-28"><div className={cn("overflow-hidden rounded-medium border transition-opacity duration-300", isCurrent ? "border-B-500" : "border-Gray-400", isExpanded ? "opacity-100" : isCurrent ? "opacity-100" : "border-transparent bg-Gray-white")}><button type="button" onClick={() => toggleSection(section_0.id)} className={cn("flex w-full items-center justify-between px-20 py-16 transition-colors duration-200", "focus:outline-none focus-visible:ring-2 focus-visible:ring-B-400", isExpanded && "sticky top-0", isCurrent ? "bg-B-25" : "bg-Gray-white", isExpanded && !isCurrent && "bg-Gray-50")}><div className="flex flex-col items-start"><p className="font-600 text-Gray-700 typo-body4">{section_0.title}</p>{section_0.subtitle && <p className="font-600 text-Gray-500 typo-caption1">{section_0.subtitle}</p>}</div><div className="flex items-center gap-8"><p className="font-600 text-Gray-550 typo-body4">{section_0.count === 0 ? `${section_0.totalCount}건` : isCurrent || filter && filter !== "available" ? <><span className="text-B-500">{section_0.count}건</span><span className="text-Gray-550">/{section_0.totalCount}건</span></> : `${section_0.count}건`}</p>{isExpanded ? <SvgChevronup className={cn("size-12", isCurrent ? "text-B-500" : "text-Gray-600")} /> : <SvgChevrondown className={cn("size-12", isCurrent ? "text-B-500" : "text-Gray-600")} />}</div></button><div className={cn("grid transition-all duration-300", isExpanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]")} style={{
            transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)"
          }}><div className="overflow-hidden"><div ref={el => {
                sectionRefs.current[section_0.id] = el;
              }} className="max-h-[75vh] overflow-y-auto bg-Gray-white hide-scrollbar webkit-scroll-touch"><ul className="flex flex-col">{section_0.contents.map(_temp2)}</ul></div></div></div></div></div></div>;
  });
  let t4;
  if ($[3] !== t3) {
    t4 = <div className={t2}>{t3}</div>;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  return t4;
};
function _temp(state) {
  return state.myInfo?.age;
}
function _temp2(policy) {
  return <li key={policy.id}><TimelineCard policy={policy} /></li>;
}